import { useQuery } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { functions } from "src/api/firebase";
import { EnhancedPlan } from "../pages/SubscriptionPage/types";
import { fallbackPlans } from "../pages/SubscriptionPage/data/planFeatures";

// -----------------------------
// Types
// -----------------------------
export interface PayPalPlan {
    id: string;
    name: string;
    product_id: string;
    status: string;
    billing_cycles: Array<{
        tenure_type: string;
        sequence: number;
        pricing_scheme: {
            fixed_price: {
                value: string;
                currency_code: string;
            };
        };
        frequency: {
            interval_unit: string;
            interval_count: number;
        };
    }>;
}

export interface PayPalProduct {
    id: string;
    name: string;
    description: string;
}

export interface PayPalPlansResponse {
    plans: PayPalPlan[];
    products: PayPalProduct[];
}

// -----------------------------
// Firebase Callable Functions
// -----------------------------
const getPayPalPlansFunction = httpsCallable(functions, "getPayPalPlans");

// -----------------------------
// Data Fetching Functions
// -----------------------------
export const fetchPayPalPlans = async (): Promise<EnhancedPlan[]> => {
    const result = await getPayPalPlansFunction();
    const data = result.data as PayPalPlansResponse;

    // Define the free plan
    const freePlan: EnhancedPlan = {
        id: "free-plan",
        name: "Basic",
        description: "Get started with AI-powered interview practice",
        price: 0,
        currency: "USD",
        interval: "",
        features: [
            "5 AI-powered mock interviews per month",
            "Limited question bank (50+ questions)",
            "Basic performance report",
            "Email summary after each session",
        ],
        productId: "free-product",
    };

    // Process plans data
    const paypalPlans = data.plans || [];
    const products = data.products || [];

    // Transform the plans
    let enhancedPlans: EnhancedPlan[] = paypalPlans
        .filter((plan) => plan.status === "ACTIVE")
        .map((plan) => {
            // Find associated product
            const product = products.find((p) => p.id === plan.product_id) || {
                name: "Unknown",
                description: "",
            };

            let price = 0;
            let currency = "USD";
            let interval = "";
            let features: string[] = [];

            // Extract price details from billing cycles
            if (plan.billing_cycles && Array.isArray(plan.billing_cycles)) {
                const priceCycle = plan.billing_cycles.find(
                    (cycle) => cycle.tenure_type === "REGULAR"
                );

                if (
                    priceCycle &&
                    priceCycle.pricing_scheme &&
                    priceCycle.pricing_scheme.fixed_price
                ) {
                    price = parseFloat(
                        priceCycle.pricing_scheme.fixed_price.value || "0"
                    );
                    currency =
                        priceCycle.pricing_scheme.fixed_price.currency_code ||
                        "USD";

                    if (priceCycle.frequency) {
                        const count = priceCycle.frequency.interval_count || 1;
                        const unit =
                            priceCycle.frequency.interval_unit || "MONTH";
                        interval = `${count} ${unit.toLowerCase()}${
                            count > 1 ? "s" : ""
                        }`;
                    }
                }
            }

            // Extract features from product description
            try {
                if (product?.description) {
                    // Try parsing as JSON
                    try {
                        const featuresData = JSON.parse(product.description);
                        if (Array.isArray(featuresData.features)) {
                            features = featuresData.features;
                        }
                    } catch (e) {
                        // If not JSON, use as single feature or split by commas
                        features = product.description.includes(",")
                            ? product.description
                                  .split(",")
                                  .map((f: string) => f.trim())
                            : [product.description];
                    }
                }
            } catch (e) {
                // Use default features if extraction fails
                features = ["Subscription benefits"];
            }

            // Determine if this plan should be highlighted or marked as popular
            const isProPlan = plan.name.toLowerCase().includes("pro");
            const isPlusPlan = plan.name.toLowerCase().includes("plus");

            // Look for trial period
            const trialCycle = plan.billing_cycles.find(
                (cycle) => cycle.tenure_type === "TRIAL"
            );
            let trialPeriod;
            if (trialCycle && trialCycle.frequency) {
                trialPeriod = {
                    intervalUnit: trialCycle.frequency.interval_unit,
                    intervalCount: trialCycle.frequency.interval_count,
                    formatted: `${
                        trialCycle.frequency.interval_count
                    } ${trialCycle.frequency.interval_unit.toLowerCase()}${
                        trialCycle.frequency.interval_count > 1 ? "s" : ""
                    }`,
                };
            }

            // Return enhanced plan object
            return {
                id: plan.id,
                name: plan.name,
                description: product?.name || "",
                price: price,
                currency: currency,
                interval: interval,
                features: features,
                highlighted: isProPlan,
                mostPopular: isPlusPlan,
                badge: isProPlan
                    ? "Best Value"
                    : isPlusPlan
                    ? "Most Popular"
                    : undefined,
                productId: plan.product_id,
                trialPeriod: trialPeriod,
            };
        });

    // Sort plans by price
    enhancedPlans.sort((a, b) => a.price - b.price);

    // Always add the free plan at the beginning
    enhancedPlans = [freePlan, ...enhancedPlans];

    return enhancedPlans;
};

// -----------------------------
// Custom React Query Hooks
// -----------------------------
export function usePayPalPlans(
    options: {
        isAuthenticated?: boolean;
    } = {}
) {
    const { isAuthenticated = true } = options;

    const query = useQuery<EnhancedPlan[]>({
        queryKey: ["paypalPlans"],
        queryFn: fetchPayPalPlans,
        enabled: isAuthenticated, // Only run query if user is authenticated
        staleTime: 1000 * 60 * 10, // 10 minutes before refetching
        cacheTime: 1000 * 60 * 30, // 30 minutes cache time
        retry: 2, // Retry failed requests up to 2 times
    });

    // If there's an error, provide fallback plans
    const data = query.isError
        ? [
              // Define the free plan
              {
                  id: "free-plan",
                  name: "Basic",
                  description: "Get started with AI-powered interview practice",
                  price: 0,
                  currency: "USD",
                  interval: "",
                  features: [
                      "5 AI-powered mock interviews per month",
                      "Limited question bank (50+ questions)",
                      "Basic performance report",
                      "Email summary after each session",
                  ],
                  productId: "free-product",
              },
              ...fallbackPlans.filter((plan) => plan.price > 0),
          ]
        : query.data;

    return {
        data,
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,
    };
}
